/**
 * Theme Toggle Component
 *
 * A dropdown menu for switching between light, dark, and system themes.
 */

import { Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUIStore, selectTheme, selectResolvedTheme, type Theme } from '@/stores/uiStore';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  variant?: 'icon' | 'button';
  className?: string;
}

export function ThemeToggle({ variant = 'icon', className }: ThemeToggleProps) {
  const theme = useUIStore(selectTheme);
  const resolvedTheme = useUIStore(selectResolvedTheme);
  const setTheme = useUIStore((state) => state.setTheme);

  const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  const CurrentIcon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === 'icon' ? 'icon' : 'sm'}
          className={cn(
            'relative',
            variant === 'button' && 'justify-start gap-2',
            className
          )}
          aria-label={`Theme: ${theme}. Current: ${resolvedTheme}`}
        >
          <Sun
            className={cn(
              'h-5 w-5 transition-all',
              resolvedTheme === 'dark' && 'rotate-90 scale-0'
            )}
            aria-hidden="true"
          />
          <Moon
            className={cn(
              'absolute h-5 w-5 transition-all',
              variant === 'button' ? 'left-3' : 'left-1/2 -translate-x-1/2',
              resolvedTheme === 'light' && '-rotate-90 scale-0'
            )}
            aria-hidden="true"
          />
          {variant === 'button' && (
            <span className="ml-4">
              {theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light'}
            </span>
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {themeOptions.map(({ value, label, icon: Icon }) => (
          <DropdownMenuItem
            key={value}
            onClick={() => setTheme(value)}
            className={cn(
              'flex items-center gap-2',
              theme === value && 'bg-accent'
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span>{label}</span>
            {theme === value && (
              <span className="ml-auto text-xs text-muted-foreground">
                {value === 'system' && `(${resolvedTheme})`}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Inline Theme Toggle
 *
 * A simpler toggle that cycles through themes or just toggles dark/light.
 */
export function ThemeToggleInline({ className }: { className?: string }) {
  const resolvedTheme = useUIStore(selectResolvedTheme);
  const setTheme = useUIStore((state) => state.setTheme);

  const toggle = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className={className}
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`}
    >
      <Sun
        className={cn(
          'h-5 w-5 transition-all',
          resolvedTheme === 'dark' && 'rotate-90 scale-0'
        )}
        aria-hidden="true"
      />
      <Moon
        className={cn(
          'absolute h-5 w-5 transition-all',
          resolvedTheme === 'light' && '-rotate-90 scale-0'
        )}
        aria-hidden="true"
      />
    </Button>
  );
}
